import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

/**
 * Light, dark, or follow the system. A per-device preference: it lives in
 * this browser, like the other view settings, and applies the moment it is
 * picked. index.html reads the same key before the app loads, so a dark
 * reload never flashes white.
 */

export type ThemePref = 'light' | 'dark' | 'system';
export type Theme = 'light' | 'dark';

export const THEME_KEY = 'deltapicks.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

function readPref(): ThemePref {
  try {
    const v = localStorage.getItem(THEME_KEY);
    return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
  } catch {
    return 'system';
  }
}

const systemTheme = (): Theme => (window.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light');
const resolve = (pref: ThemePref): Theme => (pref === 'system' ? systemTheme() : pref);

function apply(theme: Theme) {
  const root = document.documentElement;
  root.dataset.theme = theme;
  // The browser chrome on phones follows the page.
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', getComputedStyle(root).getPropertyValue('--bg').trim());
}

type Ctx = { pref: ThemePref; theme: Theme; setPref: (p: ThemePref) => void; toggle: () => void };
const ThemeContext = createContext<Ctx | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pref, setPrefState] = useState<ThemePref>(readPref);
  const [theme, setTheme] = useState<Theme>(() => resolve(readPref()));

  useEffect(() => {
    const next = resolve(pref);
    setTheme(next);
    apply(next);
    if (pref !== 'system') return;
    // Following the system: change when the OS does.
    const m = window.matchMedia(DARK_QUERY);
    const onChange = () => {
      const t = systemTheme();
      setTheme(t);
      apply(t);
    };
    m.addEventListener('change', onChange);
    return () => m.removeEventListener('change', onChange);
  }, [pref]);

  const setPref = useCallback((p: ThemePref) => {
    setPrefState(p);
    try {
      localStorage.setItem(THEME_KEY, p);
    } catch {
      /* private window: the choice holds for this visit */
    }
  }, []);

  const toggle = useCallback(() => setPref(theme === 'dark' ? 'light' : 'dark'), [theme, setPref]);

  return <ThemeContext.Provider value={{ pref, theme, setPref, toggle }}>{children}</ThemeContext.Provider>;
}

export function useTheme(): Ctx {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme outside ThemeProvider');
  return ctx;
}
